import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { ScreenWidth, ScreenWidthValues } from '../../styles';
import { ScreenContext } from './context';

const MD_TAILWIND_BREAKPOINT = 768;
const LG_TAILWIND_BREAKPOINT = 1080;

/**
 * Iterates over ScreenWidth values from small to large, looking for the nearest screen size
 * that is larger than the current dimensions. E.g. if the screen is 2300 px wide it will return 2400px
 * */
const getNearestScreenWidth = () => {
  const sortedScreenWidth = Object.values(ScreenWidth).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  for (const sz of sortedScreenWidth) {
    const match = window.matchMedia(`(max-width: ${sz})`).matches;
    if (match) {
      // match==true means it is smaller than SZ
      return sz; // Always return 1 size larger
    }
  }
  return ScreenWidth.EXTRA_LARGE;
};

export interface IWindowDimension {
  width: number;
  height: number;
}

const SMALL_SCREEN_WIDTH = parseInt(ScreenWidth.SMALL.replace('px', ''), 10);

const MEDIUM_SCREEN_WIDTH = parseInt(ScreenWidth.MEDIUM.replace('px', ''), 10);

function getWindowDimensions(): IWindowDimension {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}
export const ScreenContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(getWindowDimensions().width <= SMALL_SCREEN_WIDTH);
  const [isSmallTailwindScreen, setIsSmallTailwindScreen] = useState<boolean>(
    getWindowDimensions().width < MD_TAILWIND_BREAKPOINT
  );
  const [isMediumScreen, setIsMediumScreen] = useState<boolean>(
    getWindowDimensions().width <= MEDIUM_SCREEN_WIDTH && getWindowDimensions().width > SMALL_SCREEN_WIDTH
  );
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(getWindowDimensions().width > MEDIUM_SCREEN_WIDTH);
  const [isMediumTailwindScreen, setIsMediumTailwindScreen] = useState<boolean>(
    getWindowDimensions().width <= LG_TAILWIND_BREAKPOINT && getWindowDimensions().width >= MD_TAILWIND_BREAKPOINT
  );
  const [isLargeTailwindScreen, setIsLargeTailwindScreen] = useState<boolean>(
    getWindowDimensions().width >= LG_TAILWIND_BREAKPOINT
  );
  const [nearestScreenWidth, setNearestScreenWidth] = useState<ScreenWidth>(getNearestScreenWidth());
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    const handleResize = debounce(() => {
      const newWindowDimensions = getWindowDimensions();
      const isNowLargeScreen = newWindowDimensions.width > MEDIUM_SCREEN_WIDTH;
      setIsLargeScreen(isNowLargeScreen);

      const isNowLargeTailwindScreen = newWindowDimensions.width >= LG_TAILWIND_BREAKPOINT;
      setIsLargeTailwindScreen(isNowLargeTailwindScreen);

      const isNowMediumScreen =
        newWindowDimensions.width <= MEDIUM_SCREEN_WIDTH && newWindowDimensions.width > SMALL_SCREEN_WIDTH;
      setIsMediumScreen(isNowMediumScreen);

      const isNowMediumTailwindScreen =
        newWindowDimensions.width <= LG_TAILWIND_BREAKPOINT && newWindowDimensions.width >= MD_TAILWIND_BREAKPOINT;
      setIsMediumTailwindScreen(isNowMediumTailwindScreen);

      const isNowSmallScreen = newWindowDimensions.width <= SMALL_SCREEN_WIDTH;
      setIsSmallScreen(isNowSmallScreen);

      const isNowSmallTailwindScreen = newWindowDimensions.width < MD_TAILWIND_BREAKPOINT;
      setIsSmallTailwindScreen(isNowSmallTailwindScreen);

      setNearestScreenWidth(getNearestScreenWidth());
      setWindowDimensions(newWindowDimensions);
    }, 100);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [windowDimensions, nearestScreenWidth]);

  const value = useMemo(
    () => ({
      isSmallScreen,
      isMediumScreen,
      isLargeScreen,
      isSmallTailwindScreen,
      isMediumTailwindScreen,
      isLargeTailwindScreen,
      nearestScreenWidth,
      nearestScreenWidthValue: ScreenWidthValues[nearestScreenWidth],
      windowDimensions,
    }),
    [isSmallScreen, isMediumScreen, isLargeScreen, nearestScreenWidth, windowDimensions]
  );

  return <ScreenContext.Provider value={value}>{children}</ScreenContext.Provider>;
};
